"use strict";Object.defineProperty(exports, "__esModule", {value: true});var _react = require('react');
var _lodash = require('lodash');

const isEmptyValue = (value) =>
  _lodash.isNil.call(void 0, value) || (typeof value === 'string' && !value.trim())








/*
 * When a sign-in or sign-up field shows its error. The form computes errors
 * whenever it likes; this decides which ones are on screen:
 *
 * - A field's error is revealed when the person typed into it and then left
 *   it with a value (a format error), or when the form is submitted (any
 *   error, "required" included).
 * - A revealed error stays while the field still has an error, so it changes
 *   or clears as they fix the value. Once it clears, the field is hidden
 *   again, so a new error never appears while they are typing.
 * - A focus or blur without typing (autofocus, a password manager's inline
 *   button taking focus on load) reveals nothing.
 *
 * `errors` maps each field name to its current error, or undefined.
 */
 const useFieldErrorDisplay = (errors) => {
  const typedRef = _react.useRef.call(void 0, new Set())
  // Revealed fields whose error has been on screen since they were revealed.
  const seenRef = _react.useRef.call(void 0, new Set())
  const [revealed, setRevealed] = _react.useState(() => new Set())

  const hide = _react.useCallback.call(void 0, (name) => {
    seenRef.current.delete(name)
    setRevealed(r => {
      if (!r.has(name)) return r
      const next = new Set(r)
      next.delete(name)
      return next
    })
  }, [])

  const reveal = _react.useCallback.call(void 0, (names) => {
    setRevealed(r => (names.every(n => r.has(n)) ? r : new Set([...r, ...names])))
  }, [])

  // The person changed the value. A field revealed on leaving that never had
  // an error to show (it was valid) is hidden again before they type on.
  const onType = _react.useCallback.call(void 0, (name) => {
    typedRef.current.add(name)
    if (!seenRef.current.has(name)) hide(name)
  }, [hide])

  const onLeave = _react.useCallback.call(void 0, (name, value) => {
    const hasTyped = typedRef.current.has(name)
    typedRef.current.delete(name)
    if (hasTyped && !isEmptyValue(value)) reveal([name])
  }, [reveal])

  // Wraps a web field's registration (react-hook-form's register()) so its
  // changes and blurs are seen here before the form's own handlers.
  const bind = _react.useCallback.call(void 0, (field) => ({
    ...field,
    onChange: (e) => {
      onType(field.name)
      return field.onChange(e)
    },
    onBlur: (e) => {
      onLeave(field.name, e.target.value)
      return field.onBlur(e)
    },
  }), [onType, onLeave])

  // For the form's submit: reveal every field that has an error now.
  const onSubmitErrors = _react.useCallback.call(void 0, (names) => reveal(names), [reveal])

  const withError = Object.keys(errors).filter(name => !!errors[name]).sort().join('|')
  _react.useEffect.call(void 0, () => {
    const failing = new Set(withError ? withError.split('|') : [])
    revealed.forEach(name => {
      if (failing.has(name)) seenRef.current.add(name)
      else if (seenRef.current.has(name)) hide(name)
    })
  }, [withError, revealed, hide])

  const shown = _react.useCallback.call(void 0, 
    (name) => (revealed.has(name) ? errors[name] : undefined),
    // `errors` is read fresh each render; `withError` tracks which fields fail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revealed, withError, errors],
  )

  return { shown, onType, onLeave, onSubmitErrors, bind }
}; exports.useFieldErrorDisplay = useFieldErrorDisplay

// What a field component needs from the display (passed down as a prop).


exports. default = exports.useFieldErrorDisplay
